import React, { useContext, useEffect, useRef } from 'react'
import { FormProvider, useFormContext } from 'react-hook-form'
import { useAddressForm } from './address-form.hooks'
import TextInputField from '../../fields/text-input-field/text-input-field.component'
import { Grid2 } from '@mui/material'
import {
  SettingsContext,
  SettingsProviderValue,
} from 'pages/settings/settings.provider'
import FormSingleColumnLayout from 'layouts/form-single-column-layout/form-single-column-layout'
import AutocompleteAddressField from '../../fields/autocomplete-address-field/autocomplete-address-field.component'

const AddressFormFields = () => {
  const { watch } = useFormContext()
  const { user, setUser } = useContext(
    SettingsContext
  ) as unknown as SettingsProviderValue

  // Watch form values and sync to user context
  const addressLine1 = watch('addressLine1')
  const addressLine2 = watch('addressLine2')
  const city = watch('city')
  const province = watch('province')
  const postalCode = watch('postalCode')

  // Parse addressLine1 to extract streetNumber and streetName
  useEffect(() => {
    if (addressLine1 !== undefined) {
      if (addressLine1) {
        // Try to parse street number and street name from addressLine1
        // Format is typically "2010 Winston Park Drive" or "200 Street Name"
        const parts = addressLine1.trim().split(/\s+/)
        if (parts.length > 0) {
          const firstPart = parts[0]
          // Check if first part is a number
          if (/^\d+$/.test(firstPart)) {
            setUser({
              ...user,
              streetNumber: firstPart,
              streetName: parts.slice(1).join(' '),
            })
          } else {
            // No street number, use entire addressLine1 as streetName
            setUser({
              ...user,
              streetNumber: '',
              streetName: addressLine1,
            })
          }
        }
      } else {
        setUser({
          ...user,
          streetNumber: '',
          streetName: '',
        })
      }
    }
  }, [addressLine1, setUser, user])

  // Sync addressLine2 to unitNumber
  useEffect(() => {
    if (addressLine2 !== undefined) {
      setUser({
        ...user,
        unitNumber: addressLine2 || '',
      })
    }
  }, [addressLine2, setUser, user])

  // Sync city, province, postalCode
  useEffect(() => {
    if (city !== undefined || province !== undefined || postalCode !== undefined) {
      setUser({
        ...user,
        city: city || '',
        state: province || '',
        zipCode: postalCode || '',
      })
    }
  }, [city, province, postalCode, setUser, user])

  return (
    <form autoComplete="off">
      <Grid2 container spacing={2}>
        <FormSingleColumnLayout>
          <AutocompleteAddressField
            label="Address line 1*"
            name="addressLine1"
            placeholder=""
          />
        </FormSingleColumnLayout>

        <FormSingleColumnLayout>
          <TextInputField
            label="Address line 2"
            name="addressLine2"
            placeholder=""
            autoComplete="off"
          />
        </FormSingleColumnLayout>

        <FormSingleColumnLayout>
          <TextInputField label="City*" name="city" placeholder="" autoComplete="off" />
        </FormSingleColumnLayout>

        <FormSingleColumnLayout>
          <TextInputField label="Province*" name="province" placeholder="" autoComplete="off" />
        </FormSingleColumnLayout>

        <FormSingleColumnLayout>
          <TextInputField label="Postal code*" name="postalCode" placeholder="" autoComplete="off" />
        </FormSingleColumnLayout>
      </Grid2>
    </form>
  )
}

const AddressForm = () => {
  const formMethods = useAddressForm()
  const { user } = useContext(
    SettingsContext
  ) as unknown as SettingsProviderValue
  const initializedRef = useRef(false)

  // Initialize form values from user data (only once when user data is available)
  useEffect(() => {
    if (!user || initializedRef.current) return
    
    // Build addressLine1 from streetNumber and streetName
    const addressLine1 = [user?.streetNumber, user?.streetName]
      .filter(Boolean)
      .join(' ')
      .trim()

    formMethods.setValue('addressLine1', addressLine1 || '', { shouldDirty: false })
    formMethods.setValue('addressLine2', user?.unitNumber || '', { shouldDirty: false })
    formMethods.setValue('city', user?.city || '', { shouldDirty: false })
    formMethods.setValue('province', user?.state || '', { shouldDirty: false })
    formMethods.setValue('postalCode', user?.zipCode || '', { shouldDirty: false })
    
    initializedRef.current = true
  }, [user, formMethods])

  return (
    <FormProvider {...formMethods}>
      <AddressFormFields />
    </FormProvider>
  )
}

export default AddressForm
